// src/components/AuthLayout.js
import React from 'react';
import { Box, Container, CssBaseline, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from './Header';
import Footer from './Footer';
import BrandMark from './BrandMark';
import SiteBackdrop from './SiteBackdrop';
import { SITE } from '../siteInfo';

/**
 * The frame the login and signup pages share, so the two can never drift into
 * looking like different sites. The form sits on the left; the field panel on
 * the right is decoration and is dropped entirely on small screens rather than
 * squashed into a letterbox above the form.
 */
const AuthLayout = ({ title, subtitle, children }) => {
  const dark = useTheme().palette.mode === 'dark';

  return (
  <>
    <CssBaseline />
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />

      <Container
        maxWidth="lg"
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          py: { xs: 4, md: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          <Box
            sx={{
              p: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ mt: 1, color: 'text.secondary' }}>{subtitle}</Typography>
            )}
            <Box sx={{ mt: 3.5 }}>{children}</Box>
          </Box>

          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'relative',
              minHeight: 540,
              alignItems: 'flex-end',
              p: 5,
              // The site's background photo, beneath the wash and the type.
              isolation: 'isolate',
              // The photograph is bright and busy; the wash is what makes the
              // type on top of it legible rather than merely present.
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: dark
                  ? 'linear-gradient(160deg, rgba(25,30,15,0.80) 0%, rgba(18,21,12,0.94) 100%)'
                  : 'linear-gradient(160deg, rgba(52,66,26,0.55) 0%, rgba(30,38,16,0.88) 100%)',
              },
            }}
          >
            {/* Lazy: the panel is hidden below md, and a hidden lazy image
                is never fetched, so a phone does not pay for it. */}
            <SiteBackdrop slot="hero" sizes="50vw" />
            <Box sx={{ position: 'relative' }}>
              <BrandMark size="lg" tone="dark" />
              <Typography
                sx={{
                  mt: 2.5,
                  maxWidth: 380,
                  fontSize: '0.95rem',
                  lineHeight: 1.75,
                  color: 'rgba(244,241,230,0.78)',
                }}
              >
                {SITE.about}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  </>
  );
};

export default AuthLayout;
